import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Dropdown,
  Option,
  Tooltip,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
  Textarea,
  RadioGroup,
  Radio,
  tokens,
} from '@fluentui/react-components';
import {
  PersonRegular,
  BuildingRegular,
  FilterRegular,
  ArrowClockwiseRegular,
  AddRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowUndoRegular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import DataTable, { type Column } from '../components/DataTable';
import { useClientOptOutsScopeParams } from './useClientOptOutsScopeParams';
import { TableSkeleton } from '../components/LoadingSkeleton';
import {
  getOptOuts,
  getOptOutStats,
  createOptOut,
  reactivateClient,
  type OptOutFilters,
} from '../services/api';
import {
  type ClientOptOut,
  type OptOutReason,
  type OptOutChannel,
  type OptOutStats,
  type CreateOptOutRequest,
  optOutReasonColors,
} from '../types';

const currentYear = new Date().getFullYear();
const defaultTaxYear = currentYear;

const channelOptions: OptOutChannel[] = ['email', 'sms', 'both'];

export default function ClientOptOutsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reasonLabels: Record<OptOutReason, string> = useMemo(() => ({
    not_filing_this_year: t('clientOptOutsPage.reasonLabels.notFilingThisYear'),
    using_different_preparer: t('clientOptOutsPage.reasonLabels.usingDifferentPreparer'),
    doing_own_taxes: t('clientOptOutsPage.reasonLabels.doingOwnTaxes'),
    deceased: t('clientOptOutsPage.reasonLabels.deceased'),
    moved_away: t('clientOptOutsPage.reasonLabels.movedAway'),
    unsubscribe_request: t('clientOptOutsPage.reasonLabels.unsubscribeRequest'),
    other: t('clientOptOutsPage.reasonLabels.other'),
  }), [t]);

  const channelLabels: Record<OptOutChannel, string> = useMemo(() => ({
    email: t('clientOptOutsPage.channelLabels.email'),
    sms: t('clientOptOutsPage.channelLabels.sms'),
    both: t('clientOptOutsPage.channelLabels.both'),
    none: t('clientOptOutsPage.channelLabels.none'),
  }), [t]);

  // Reason dropdown options, built from reasonLabels so there is one translation per reason, not
  // a copy in the dropdown and another in the badge/table rendering.
  const reasonOptions: { value: OptOutReason; label: string }[] = useMemo(() => [
    'not_filing_this_year', 'using_different_preparer', 'doing_own_taxes',
    'deceased', 'moved_away', 'unsubscribe_request', 'other',
  ].map((value) => ({ value: value as OptOutReason, label: reasonLabels[value as OptOutReason] })), [reasonLabels]);

  const sourceLabels: Record<string, string> = useMemo(() => ({
    manual: t('clientOptOutsPage.sourceLabels.manual'),
    email_reply: t('clientOptOutsPage.sourceLabels.emailReply'),
    sms_reply: t('clientOptOutsPage.sourceLabels.smsReply'),
    unsubscribe_link: t('clientOptOutsPage.sourceLabels.unsubscribeLink'),
  }), [t]);

  // These three used to be plain useState and silently reset on Back navigation or a refresh —
  // the same defect class #78 fixed on TaxReturnsPage. Second adoption of that pattern.
  const {
    reason: selectedReason, channel: selectedChannel, inactive: showInactive,
    setReason: setSelectedReason, setChannel: setSelectedChannel, setInactive: setShowInactive,
  } = useClientOptOutsScopeParams();
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Form state for adding new opt-out
  const [newClientId, setNewClientId] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientType, setNewClientType] = useState<'contact' | 'account'>('contact');
  const [newReason, setNewReason] = useState<OptOutReason>('not_filing_this_year');
  const [newReasonDetails, setNewReasonDetails] = useState('');
  const [newChannel, setNewChannel] = useState<OptOutChannel>('both');
  const [newNotes, setNewNotes] = useState('');

  // Filters
  const filters: OptOutFilters = {
    taxYear: defaultTaxYear,
    reason: selectedReason,
    channel: selectedChannel,
    isActive: showInactive ? undefined : true,
  };

  // Fetch opt-outs
  const { data: optOuts, isLoading, error, refetch } = useQuery<ClientOptOut[]>({
    queryKey: ['optOuts', filters],
    queryFn: () => getOptOuts(filters),
  });

  // Fetch stats
  const { data: stats } = useQuery<OptOutStats>({
    queryKey: ['optOutStats', defaultTaxYear],
    queryFn: () => getOptOutStats(defaultTaxYear),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (request: CreateOptOutRequest) => createOptOut(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['optOuts'] });
      queryClient.invalidateQueries({ queryKey: ['optOutStats'] });
      queryClient.invalidateQueries({ queryKey: ['eligibleClients'] });
      setAddDialogOpen(false);
      resetForm();
    },
  });

  // Reactivate mutation
  const reactivateMutation = useMutation({
    mutationFn: (id: string) => reactivateClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['optOuts'] });
      queryClient.invalidateQueries({ queryKey: ['optOutStats'] });
      queryClient.invalidateQueries({ queryKey: ['eligibleClients'] });
    },
  });

  const resetForm = () => {
    setNewClientId('');
    setNewClientName('');
    setNewClientEmail('');
    setNewClientPhone('');
    setNewClientType('contact');
    setNewReason('not_filing_this_year');
    setNewReasonDetails('');
    setNewChannel('both');
    setNewNotes('');
  };

  const clearFilters = () => {
    setSelectedReason(undefined);
    setSelectedChannel(undefined);
    setShowInactive(false);
  };

  const handleCreateOptOut = () => {
    if (!newClientId || !newClientName) return;
    createMutation.mutate({
      clientId: newClientId,
      clientName: newClientName,
      clientEmail: newClientEmail || undefined,
      clientPhone: newClientPhone || undefined,
      clientType: newClientType,
      taxYear: defaultTaxYear,
      reason: newReason,
      reasonDetails: newReasonDetails || undefined,
      optOutChannel: newChannel,
      source: 'manual',
      notes: newNotes || undefined,
    });
  };

  const handleReactivate = (optOut: ClientOptOut) => {
    if (window.confirm(t('clientOptOutsPage.reactivateConfirm', { name: optOut.clientName }))) {
      reactivateMutation.mutate(optOut.id);
    }
  };

  const columns: Column<ClientOptOut>[] = [
    {
      key: 'clientName',
      header: t('clientOptOutsPage.table.clientHeader'),
      sortable: true,
      render: (optOut) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {optOut.clientType === 'account' ? <BuildingRegular /> : <PersonRegular />}
          <div>
            <div style={{ fontWeight: 500 }}>{optOut.clientName}</div>
            <div style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-secondary)' }}>
              {optOut.clientEmail || t('clientOptOutsPage.table.noEmail')}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'reason',
      header: t('clientOptOutsPage.table.reasonHeader'),
      sortable: true,
      render: (optOut) => (
        <Tooltip content={optOut.reasonDetails || t('clientOptOutsPage.table.noAdditionalDetails')} relationship="label">
          <Badge
            appearance="filled"
            style={{
              backgroundColor: optOutReasonColors[optOut.reason],
              color: 'white',
              cursor: optOut.reasonDetails ? 'help' : 'default',
            }}
          >
            {reasonLabels[optOut.reason]}
          </Badge>
        </Tooltip>
      ),
    },
    {
      key: 'optOutChannel',
      header: t('clientOptOutsPage.table.channelHeader'),
      sortable: true,
      render: (optOut) => (
        <Badge appearance="outline" color="informative">
          {channelLabels[optOut.optOutChannel]}
        </Badge>
      ),
    },
    {
      key: 'source',
      header: t('clientOptOutsPage.table.sourceHeader'),
      sortable: true,
      render: (optOut) => sourceLabels[optOut.source] || optOut.source,
    },
    {
      key: 'optOutDate',
      header: t('clientOptOutsPage.table.optOutDateHeader'),
      sortable: true,
      getValue: (optOut) => new Date(optOut.optOutDate).getTime(),
      render: (optOut) => (
        <Tooltip content={format(new Date(optOut.optOutDate), 'MMM d, yyyy h:mm a')} relationship="label">
          <span>{formatDistanceToNow(new Date(optOut.optOutDate), { addSuffix: true })}</span>
        </Tooltip>
      ),
    },
    {
      key: 'isActive',
      header: t('clientOptOutsPage.table.statusHeader'),
      sortable: true,
      getValue: (optOut) => optOut.isActive ? 1 : 0,
      render: (optOut) => (
        optOut.isActive ? (
          <Badge appearance="filled" color="danger" icon={<DismissCircleRegular />}>
            {t('clientOptOutsPage.table.optedOutBadge')}
          </Badge>
        ) : (
          <Tooltip
            content={optOut.reactivatedAt
              ? t('clientOptOutsPage.table.reactivatedTooltip', { date: format(new Date(optOut.reactivatedAt), 'MMM d, yyyy') })
              : t('clientOptOutsPage.table.reactivatedBadge')}
            relationship="label"
          >
            <Badge appearance="filled" color="success" icon={<CheckmarkCircleRegular />}>
              {t('clientOptOutsPage.table.reactivatedBadge')}
            </Badge>
          </Tooltip>
        )
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (optOut) => (
        optOut.isActive ? (
          <Tooltip content={t('clientOptOutsPage.table.reactivateTooltip')} relationship="label">
            <Button
              appearance="subtle"
              icon={<ArrowUndoRegular />}
              size="small"
              onClick={() => handleReactivate(optOut)}
              disabled={reactivateMutation.isPending}
            >
              {t('clientOptOutsPage.table.reactivateAction')}
            </Button>
          </Tooltip>
        ) : null
      ),
    },
  ];

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{t('clientOptOutsPage.errors.loadFailed')}</p>
        <Button onClick={() => refetch()}>{t('clientOptOutsPage.errors.retry')}</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>{t('clientOptOutsPage.header.title')}</h2>
        <p>{t('clientOptOutsPage.header.subtitle', { year: defaultTaxYear })}</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{stats.total}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('clientOptOutsPage.stats.totalOptOuts')}</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#F59E0B' }}>{stats.byReason.not_filing_this_year}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('clientOptOutsPage.stats.notFiling')}</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#EF4444' }}>{stats.byReason.using_different_preparer}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('clientOptOutsPage.stats.differentPreparer')}</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#3B82F6' }}>{stats.byReason.doing_own_taxes}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('clientOptOutsPage.stats.selfFiling')}</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: tokens.fontSizeHero700, fontWeight: 600, color: '#10B981' }}>{stats.reactivated}</div>
            <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>{t('clientOptOutsPage.stats.reactivated')}</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar">
        <div className="filter-group">
          <FilterRegular />
          <Dropdown
            placeholder={t('clientOptOutsPage.toolbar.reasonPlaceholder')}
            value={selectedReason || ''}
            onOptionSelect={(_, data) => setSelectedReason(data.optionValue as OptOutReason || undefined)}
            style={{ minWidth: '180px' }}
          >
            <Option text={t('clientOptOutsPage.toolbar.allReasons')} value="">{t('clientOptOutsPage.toolbar.allReasons')}</Option>
            {reasonOptions.map((reason) => (
              <Option key={reason.value} text={reason.label} value={reason.value}>
                {reason.label}
              </Option>
            ))}
          </Dropdown>

          <Dropdown
            placeholder={t('clientOptOutsPage.toolbar.channelPlaceholder')}
            value={selectedChannel || ''}
            onOptionSelect={(_, data) => setSelectedChannel(data.optionValue as OptOutChannel || undefined)}
            style={{ minWidth: '140px' }}
          >
            <Option text={t('clientOptOutsPage.toolbar.allChannels')} value="">{t('clientOptOutsPage.toolbar.allChannels')}</Option>
            {channelOptions.map((channel) => (
              <Option key={channel} text={channelLabels[channel]} value={channel}>
                {channelLabels[channel]}
              </Option>
            ))}
          </Dropdown>

          <Button
            appearance={showInactive ? 'primary' : 'subtle'}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? t('clientOptOutsPage.toolbar.showingAll') : t('clientOptOutsPage.toolbar.showReactivated')}
          </Button>
        </div>

        <Button
          appearance="subtle"
          icon={<ArrowClockwiseRegular />}
          onClick={clearFilters}
        >
          Clear Filters
        </Button>

        <Button
          appearance="subtle"
          icon={<ArrowClockwiseRegular />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>

        <Button
          appearance="primary"
          icon={<AddRegular />}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Opt-Out
        </Button>
      </div>

      {/* Opt-Outs Table */}
      <div className="card">
        <DataTable
          data={optOuts || []}
          columns={columns}
          rowKey="id"
          searchable
          searchPlaceholder={t('clientOptOutsPage.search.placeholder')}
          searchKeys={['clientName', 'clientEmail']}
          emptyMessage={t('clientOptOutsPage.emptyMessage')}
          paginated
          initialSort={{ column: 'optOutDate', direction: 'desc' }}
        />
      </div>

      {/* Add Opt-Out Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(_, data) => {
        setAddDialogOpen(data.open);
        if (!data.open) resetForm();
      }}>
        <DialogSurface style={{ maxWidth: '500px' }}>
          <DialogBody>
            <DialogTitle>{t('clientOptOutsPage.addDialog.title')}</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Field label={t('clientOptOutsPage.addDialog.clientIdLabel')} required>
                  <Input
                    value={newClientId}
                    onChange={(_, data) => setNewClientId(data.value)}
                    placeholder={t('clientOptOutsPage.addDialog.clientIdPlaceholder')}
                  />
                </Field>

                <Field label={t('clientOptOutsPage.addDialog.clientNameLabel')} required>
                  <Input
                    value={newClientName}
                    onChange={(_, data) => setNewClientName(data.value)}
                    placeholder={t('clientOptOutsPage.addDialog.clientNamePlaceholder')}
                  />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <Field label={t('clientOptOutsPage.addDialog.emailLabel')}>
                    <Input
                      value={newClientEmail}
                      onChange={(_, data) => setNewClientEmail(data.value)}
                      placeholder={t('clientOptOutsPage.addDialog.emailPlaceholder')}
                      type="email"
                    />
                  </Field>
                  <Field label={t('clientOptOutsPage.addDialog.phoneLabel')}>
                    <Input
                      value={newClientPhone}
                      onChange={(_, data) => setNewClientPhone(data.value)}
                      placeholder={t('clientOptOutsPage.addDialog.phonePlaceholder')}
                    />
                  </Field>
                </div>

                <Field label={t('clientOptOutsPage.addDialog.clientTypeLabel')}>
                  <RadioGroup
                    value={newClientType}
                    onChange={(_, data) => setNewClientType(data.value as 'contact' | 'account')}
                    layout="horizontal"
                  >
                    <Radio value="contact" label={t('clientOptOutsPage.addDialog.individual')} />
                    <Radio value="account" label={t('clientOptOutsPage.addDialog.business')} />
                  </RadioGroup>
                </Field>

                <Field label={t('clientOptOutsPage.addDialog.reasonLabel')} required>
                  <Dropdown
                    value={newReason}
                    onOptionSelect={(_, data) => setNewReason(data.optionValue as OptOutReason)}
                    style={{ width: '100%' }}
                  >
                    {reasonOptions.map((reason) => (
                      <Option key={reason.value} text={reason.label} value={reason.value}>
                        {reason.label}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>

                <Field label={t('clientOptOutsPage.addDialog.reasonDetailsLabel')}>
                  <Input
                    value={newReasonDetails}
                    onChange={(_, data) => setNewReasonDetails(data.value)}
                    placeholder={t('clientOptOutsPage.addDialog.reasonDetailsPlaceholder')}
                  />
                </Field>

                <Field label={t('clientOptOutsPage.addDialog.channelLabel')}>
                  <RadioGroup
                    value={newChannel}
                    onChange={(_, data) => setNewChannel(data.value as OptOutChannel)}
                    layout="horizontal"
                  >
                    <Radio value="email" label={channelLabels.email} />
                    <Radio value="sms" label={channelLabels.sms} />
                    <Radio value="both" label={channelLabels.both} />
                  </RadioGroup>
                </Field>

                <Field label={t('clientOptOutsPage.addDialog.notesLabel')}>
                  <Textarea
                    value={newNotes}
                    onChange={(_, data) => setNewNotes(data.value)}
                    placeholder={t('clientOptOutsPage.addDialog.notesPlaceholder')}
                    rows={3}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setAddDialogOpen(false)}>
                {t('clientOptOutsPage.addDialog.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={handleCreateOptOut}
                disabled={!newClientId || !newClientName || createMutation.isPending}
              >
                {createMutation.isPending ? t('clientOptOutsPage.addDialog.submitting') : t('clientOptOutsPage.addDialog.submit')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
